import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import type { Product, WishProduct } from '../types/api';
import type { WishlistItem } from '../types/app';
import { apiClient } from '../services/api';
import { useToast } from './useToast';

interface UseWishlistOptions {
  callId: number;
  auth: { accessToken: string; serviceId: string | number };
  enabled?: boolean; // Defaults to true; demo mode is intercepted at the API layer
}

interface UseWishlistReturn {
  items: WishlistItem[];
  isOpen: boolean;
  isLoading: boolean;
  error: string | null;
  addItem: (product: Product, variantId?: number) => Promise<void>;
  removeItem: (wishProductId: number) => Promise<void>;
  // Breaking change (SPEC-064): toggleItem now requires a variantId so that wishlist
  // entries are keyed on (product_id, variant_id) — matching the widget's canonical
  // semantics. Callers must source variantId from the PDP selection.
  toggleItem: (product: Product, variantId: number) => Promise<void>;
  // Breaking change (SPEC-064): composite-key lookup. Returns true only when BOTH
  // product id and variant id match — prevents false positives on sibling variants.
  isWishlisted: (productId: number, variantId: number) => boolean;
  // Replaces the old productIds: number[]. Keys are `${productId}:${variantId}`.
  // Card-level consumers (ProductsPanel) should derive a product-only wrapper locally
  // via items.some(i => i.product.id === pid).
  wishlistedKeys: Set<string>;
  toggleWishlist: () => void;
  refetch: () => Promise<void>;
  totalItems: number;
}

/**
 * Map WishProduct from API response to WishlistItem for local state.
 */
function mapWishProductToWishlistItem(wp: WishProduct): WishlistItem {
  return {
    id: wp.id,
    product: wp.product,
    productVariant: wp.product_variant,
  };
}

export function useWishlist(options?: UseWishlistOptions): UseWishlistReturn {
  const [items, setItems] = useState<WishlistItem[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { addToast } = useToast();

  const enabled = options?.enabled ?? true;
  const callId = options?.callId;
  const auth = options?.auth;

  // Track mount state for stale response prevention
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  /**
   * Apply WishProductsResponse to local state.
   */
  const applyResponse = useCallback((products: WishProduct[]) => {
    if (!mountedRef.current) return;
    setItems(products.map(mapWishProductToWishlistItem));
    setError(null);
  }, []);

  /**
   * Fetch wish products from API and update local state.
   */
  const refetch = useCallback(async () => {
    if (!callId || !auth) return;
    try {
      // Show loader only on initial fetch (empty wishlist). WebSocket wishlist_updated
      // round-trips after optimistic toggleItem must stay silent for the same reason
      // as useCart.refetch — avoid swapping the rendered list for skeletons.
      if (items.length === 0) setIsLoading(true);
      const response = await apiClient.getWishProducts(callId, auth);
      applyResponse(response.products);
    } catch (err) {
      if (mountedRef.current) {
        const msg = err instanceof Error ? err.message : 'Failed to load wishlist';
        setError(msg);
      }
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [callId, auth, applyResponse, items.length]);

  // Fetch wish products on mount when enabled
  useEffect(() => {
    if (enabled && callId && auth) {
      refetch();
    }
    // Only run on mount or when enabled/callId change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, callId]);

  /**
   * Add item to wishlist via API with optimistic update.
   */
  const addItem = useCallback(async (product: Product, variantId?: number) => {
    if (!callId || !auth) return;

    // Identifier contract (explicit — see QA2/BUG-1):
    // A POSITIVE variantId always wins. `0` / undefined is the historical
    // "no variant context" sentinel used by card-level callers — it is NOT a
    // valid variant id, so we fall back to remote_obj_id (product_code).
    // Products with neither (e.g. chat-suggested products built by
    // chatProductToProduct, which carries no product_code) cannot be
    // identified to the API — fail loudly here instead of letting
    // apiClient.addWishProduct throw after an optimistic heart has been
    // painted. Callers must resolve a real variant id first.
    const hasVariant =
      typeof variantId === 'number' && Number.isFinite(variantId) && variantId > 0;
    const identifier = hasVariant
      ? { variantId }
      : product.product_code
        ? { remoteObjId: product.product_code }
        : null;

    if (!identifier) {
      const msg = 'This product could not be identified. Please try again.';
      setError(msg);
      addToast({ type: 'error', title: 'Failed to add to wishlist', message: msg });
      return;
    }

    // Optimistic: add item locally with temp ID (only once the identifier is known-good)
    const prevItems = items;
    setItems((prev) => [
      ...prev,
      {
        id: -Date.now(), // Temp ID until server responds
        product,
        productVariant: { id: variantId ?? product.id, title: product.title, price: product.price },
      },
    ]);

    try {
      const response = await apiClient.addWishProduct(callId, identifier, auth);
      applyResponse(response.products);
    } catch (err) {
      // Revert optimistic update
      if (mountedRef.current) {
        setItems(prevItems);
        const msg = err instanceof Error ? err.message : 'Failed to add to wishlist';
        setError(msg);
        addToast({ type: 'error', title: 'Failed to add to wishlist', message: msg });
      }
    }
  }, [callId, auth, items, applyResponse, addToast]);

  /**
   * Remove item from wishlist by wishProductId.
   * If DELETE response has no products array, refetch from API.
   */
  const removeItem = useCallback(async (wishProductId: number) => {
    if (!callId || !auth) return;

    // Optimistic: remove locally
    const prevItems = items;
    setItems((prev) => prev.filter((item) => item.id !== wishProductId));

    try {
      const response = await apiClient.removeWishProduct(callId, wishProductId, auth);
      // DELETE may return empty response or { success: true } without products array
      if (response && Array.isArray(response.products)) {
        applyResponse(response.products);
      } else {
        // No products array in response — refetch to get fresh state
        await refetch();
      }
    } catch (err) {
      // Revert optimistic update
      if (mountedRef.current) {
        setItems(prevItems);
        const msg = err instanceof Error ? err.message : 'Failed to remove from wishlist';
        setError(msg);
        addToast({ type: 'error', title: 'Failed to remove from wishlist', message: msg });
      }
    }
  }, [callId, auth, items, applyResponse, refetch, addToast]);

  /**
   * Toggle item in wishlist: add if not wishlisted, remove if wishlisted.
   * Uses composite (product_id, variant_id) key so sibling variants of the same
   * product toggle independently.
   */
  const toggleItem = useCallback(async (product: Product, variantId: number) => {
    const wishlistEntry = items.find(
      (item) => item.product.id === product.id && item.productVariant.id === variantId
    );
    if (wishlistEntry) {
      await removeItem(wishlistEntry.id);
    } else {
      await addItem(product, variantId);
    }
  }, [items, addItem, removeItem]);

  /**
   * Composite-key wishlist membership check: returns true only when BOTH the
   * product id and the variant id match. Widget parity (see
   * web-applications/apps/widget/.../product-detail.component.ts:604-611).
   */
  const isWishlisted = useCallback((productId: number, variantId: number): boolean => {
    return items.some(
      (item) => item.product.id === productId && item.productVariant.id === variantId
    );
  }, [items]);

  /**
   * Memoized Set of composite keys `${productId}:${variantId}` for O(1) membership
   * lookups by consumers that need to pre-compute filters. Replaces the old
   * productIds: number[] — which was too coarse to discriminate sibling variants.
   */
  const wishlistedKeys = useMemo(
    () => new Set(items.map((item) => `${item.product.id}:${item.productVariant.id}`)),
    [items]
  );

  const toggleWishlist = useCallback(() => {
    setIsOpen((prev) => !prev);
  }, []);

  const totalItems = items.length;

  return {
    items,
    isOpen,
    isLoading,
    error,
    addItem,
    removeItem,
    toggleItem,
    isWishlisted,
    wishlistedKeys,
    toggleWishlist,
    refetch,
    totalItems,
  };
}
